import { green, red } from "@ant-design/colors";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Customer } from "@lib/types/Customer";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import IntlMessages from "@lib/helpers/IntlMessages";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TableColumnsProps } from "@lib/types/General";
import { getRoute } from "@lib/helpers/RouteHelper";
import ROUTES from "@lib/constants/Routes";

type Props<T> = TableColumnsProps<T> & {
  openModal: boolean;
  handleSelectCustomer: (manager: Customer) => void;
  deleteQuery: UseMutationProps<Customer>;
};

const getColumns = <T extends Customer>(props: Props<T>): ColumnsType<T> => {
  const { deleteQuery, handleSelectCustomer, messages } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (v, r) => (
        <TableIdButton
          link={getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL, {
            customerId: r.id,
          })}
          text={`#${shortenString(r.id)}`}
          textToBeCopied={r.id}
        />
      ),
      width: "180px",
      filterType: "search",
    },
    {
      title: "common.customerName",
      dataIndex: "id",
      key: "customer.name",
      fixed: "left",
      width: "300px",
      render: (v, r) => <CustomerName customer={r} link />,
    },
    {
      title: "common.domain",
      dataIndex: "domain",
      key: "domain",
      width: "300px",
      render: (value) => (
        <TableIdButton
          link={value}
          text={shortenString(value, 35)}
          textToBeCopied={value}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.status",
      dataIndex: "enabled",
      key: "enabled",
      width: "100px",
      render: (enabled) => (
        <Tag color={enabled ? green[7] : red[7]}>
          <IntlMessages id={enabled ? "common.enabled" : "common.disabled"} />
        </Tag>
      ),
      filterType: "dropdown",
      filters: [
        { text: messages["common.enabled"] as string, value: true },
        { text: messages["common.disabled"] as string, value: false },
      ],
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "150px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      key: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="detail"
            href={getRoute(ROUTES.ADMIN.CUSTOMER.DETAIL, {
              customerId: record.id,
            })}
            noLabel
          />

          <ActionButton
            type="edit"
            onClick={() => handleSelectCustomer(record)}
            noLabel
          />

          <ActionButton
            type="delete"
            onClick={() => deleteQuery.mutate(record)}
            itemName="common.customer"
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
